import { Assert } from "../../../../Assert";
import { IDataSource } from "../../../../Data/DataSource";
import { PartialNullable } from "../../../../Extensions";
import { IJNativeEvent } from "../../../../JNative";
import { PropertyChange } from "../../../../PropertyChange";
import { Property } from "../../../../RadObject";
import { IControl } from "../../../../Ui/Controls/Control";
import { Evaluator } from "./Evaluator";
import { IEvaluator } from "./IEvaluator";
import { EvaluatorListPropertyValues, IEvaluatorList } from "./IEvaluatorList";
import { NAMESPACE } from "./_Namespace";

export abstract class EvaluatorList<TPropertyValues extends EvaluatorListPropertyValues = EvaluatorListPropertyValues> extends Evaluator<TPropertyValues> implements IEvaluatorList
{
    // #region Static

    public static override classNamespace = NAMESPACE + "EvaluatorList";
    public static override className = "EvaluatorList";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<EvaluatorListPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                evaluators: [],
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public evaluators!: Property<IEvaluator[]>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        // Trigger Property Changes
        this.evaluators.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public addEvaluator(evaluator: IEvaluator)
    {
        evaluator.on("valueChanged", { self: this }, this.onEvaluatorValueChanged);
        evaluator.context.set(this.context.get());
        this.evaluators.get()?.push(evaluator);
        this.evaluate();
    }
    public removeEvaluator(evaluator: IEvaluator)
    {
        const
            evaluators = this.evaluators.get();

        Assert.isNotNullOrUndefined(evaluators, "evaluators");

        const
            indexOfEvaluator = evaluators.indexOf(evaluator);

        if (indexOfEvaluator >= 0)
        {
            evaluators[indexOfEvaluator].off("valueChanged", { self: this }, this.onEvaluatorValueChanged);
            evaluators[indexOfEvaluator].context.set(null);
            this.evaluators.set(evaluators.splice(indexOfEvaluator, 1));
            this.evaluate();
        }
    }

    // #endregion

    // #region Protected Methods

    protected _evaluatorsChanged(change: PropertyChange<IEvaluator[]>)
    {
        if (change.originalValue)
        {
            for (const evaluator of change.originalValue)
            {
                evaluator.off("valueChanged", { self: this }, this.onEvaluatorValueChanged);
                evaluator.context.set(null);
            }
        }

        if (change.currentValue)
        {
            for (const evaluator of change.currentValue)
            {
                evaluator.on("valueChanged", { self: this }, this.onEvaluatorValueChanged);
                evaluator.context.set(this.context.get());
            }
        }

        this.evaluate();
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: EvaluatorListPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.evaluators = new Property<IEvaluator[]>(this, "System.Object, System.Private.CoreLib", propertyValues.evaluators, this._evaluatorsChanged);
    }

    protected override _contextChanged(change: PropertyChange<IControl | IDataSource>)
    {
        this.evaluators.get()?.forEach(item => { item.context.set(change.currentValue); });

        super._contextChanged(change);
    }
    protected override _stateChanged(change: PropertyChange<any>)
    {
        this.evaluators.get()?.forEach(item => { item.state.set(change.currentValue); });

        super._stateChanged(change);
    }

    public override clone(): this
    {
        const
            evaluator = super.clone() as this;

        evaluator.evaluators.set(this.evaluators.get()?.map(item => item.clone()) ?? null);

        return evaluator;
    }
    public override flatten(): IEvaluator[]
    {
        const
            evaluators = this.evaluators.get() ?? [];

        return [
            this,
            ...evaluators,
        ]
    }

    // #endregion

    // #region Event Handlers

    protected onEvaluatorValueChanged(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this;

        self.evaluate();
    }

    // #endregion

    // #endregion
}

